"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tooltip = exports.TooltipDelayMS = exports.InfoIcon = exports.TooltipPosition = void 0;
var tslib_1 = require("tslib");
var NotificationInfoOutlineIcon_1 = require("@tableau/icons/NotificationInfoOutlineIcon");
var React = tslib_1.__importStar(require("react"));
var TypeStyle = tslib_1.__importStar(require("typestyle"));
var Localize_1 = require("../../../gen-src/Localize");
var Icons_1 = require("../../icons/Icons");
var FocusIndicators_1 = require("../../styles/FocusIndicators");
var DomUtil_1 = require("../../utils/DomUtil");
var Overlay_1 = require("../../utils/Overlay");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
// eslint-disable-next-line max-len
var TooltipConsts_1 = require("./TooltipConsts");
Object.defineProperty(exports, "TooltipPosition", { enumerable: true, get: function () { return TooltipConsts_1.TooltipPosition; } });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPromiseLike(o) {
    return typeof o === 'object' && 'then' in o && typeof o.then === 'function';
}
var InfoIcon = function (props) {
    var className = props.className, style = props.style, restProps = tslib_1.__rest(props, ["className", "style"]);
    return React.createElement(NotificationInfoOutlineIcon_1.NotificationInfoOutlineIcon, tslib_1.__assign({ alt: Localize_1.Messages.tooltipAnchorAlt(), color: '#666', size: 18 }, restProps, { className: TypeStyle.classes(className, FocusIndicators_1.FocusVisibleIndicator), style: tslib_1.__assign(tslib_1.__assign({}, Icons_1.IconStyle), style) }));
};
exports.InfoIcon = InfoIcon;
exports.TooltipDelayMS = 250;
/**
 * Wrapper which displays a popup when the child element receives focus or is hovered.
 *
 * **NOTE**: this component is for static tooltip content only. It is not WCAG-compliant to put interactive content
 * inside the tooltip. For interactive content, use `PopoverWidget`.
 *
 * @example
 *   <Tooltip tooltipContent='Last updated yesterday' />
 * @example
 *   <Tooltip tooltipContent='Some interesting info'>
 *     <span>Hover here for more info</span>
 *   </Tooltip>
 */
var Tooltip = /** @class */ (function (_super) {
    tslib_1.__extends(Tooltip, _super);
    function Tooltip() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.tooltipId = DomUtil_1.generateId();
        _this.delayTimeoutId = undefined;
        _this.tooltipContentElement = null;
        _this.state = {
            anchorClientRect: undefined,
            content: undefined,
            width: undefined,
        };
        _this.clearTimeout = function () {
            window.clearTimeout(_this.delayTimeoutId);
            _this.delayTimeoutId = undefined;
        };
        _this.fixWidth = function () {
            if (_this.state.content && _this.state.anchorClientRect && _this.tooltipContentElement && _this.state.width === undefined) {
                _this.setState({ width: _this.tooltipContentElement.getBoundingClientRect().width + TooltipConsts_1.AggregateTooltipPadding });
            }
        };
        _this.handleTooltipOverlayContentRef = function (element) {
            _this.tooltipContentElement = element ? element.firstElementChild : null;
        };
        _this.handleFocus = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (!_this.state.width) { // focus events don't have coordinates; make sure the tooltip doesn't jump if it's pointer-focused
                _this.setAnchorClientRectFromEvent(e);
            }
            _this.showTooltipOverlay(e.currentTarget);
        }, function (e) { var _a, _b, _c; return (_c = (_a = _this.props.children) === null || _a === void 0 ? void 0 : (_b = _a.props).onFocus) === null || _c === void 0 ? void 0 : _c.call(_b, e); });
        _this.handleBlur = ReactHelpers_1.mergeEventHandlers(function () {
            _this.dismissTooltipOverlay();
        }, function (e) { var _a, _b, _c; return (_c = (_a = _this.props.children) === null || _a === void 0 ? void 0 : (_b = _a.props).onBlur) === null || _c === void 0 ? void 0 : _c.call(_b, e); });
        _this.handleKeyUp = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (_this.state.content && _this.state.anchorClientRect && e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                _this.dismissTooltipOverlay();
            }
        }, function (e) { var _a, _b, _c; return (_c = (_a = _this.props.children) === null || _a === void 0 ? void 0 : (_b = _a.props).onKeyUp) === null || _c === void 0 ? void 0 : _c.call(_b, e); });
        _this.handleMouseEnter = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (_this.state.width) {
                return;
            } // skip if already showing
            var currentTarget = e.currentTarget;
            _this.setAnchorClientRectFromEvent(e); // do it now so the rect can update in subsequent mousemove events even before a delayed show
            if (_this.props.afterDelay && _this.delayTimeoutId === undefined) {
                _this.delayTimeoutId = window.setTimeout(function () { return _this.showTooltipOverlay(currentTarget); }, exports.TooltipDelayMS);
            }
            else {
                _this.showTooltipOverlay(currentTarget);
            }
        }, function (e) { var _a, _b, _c; return (_c = (_a = _this.props.children) === null || _a === void 0 ? void 0 : (_b = _a.props).onMouseEnter) === null || _c === void 0 ? void 0 : _c.call(_b, e); });
        _this.handleMouseMove = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (!_this.props.followCursor) {
                return;
            }
            _this.setAnchorClientRectFromEvent(e);
            if (!_this.state.width && _this.delayTimeoutId === undefined) {
                // show this tooltip even if another tooltip was shown (e.g. by a focus event) after the mouse entered
                _this.handleMouseEnter(e);
            }
        }, function (e) { var _a, _b, _c; return (_c = (_a = _this.props.children) === null || _a === void 0 ? void 0 : (_b = _a.props).onMouseMove) === null || _c === void 0 ? void 0 : _c.call(_b, e); });
        _this.handleMouseLeave = ReactHelpers_1.mergeEventHandlers(function () {
            _this.dismissTooltipOverlay();
        }, function (e) { var _a, _b, _c; return (_c = (_a = _this.props.children) === null || _a === void 0 ? void 0 : (_b = _a.props).onMouseLeave) === null || _c === void 0 ? void 0 : _c.call(_b, e); });
        _this.showTooltipOverlay = function (anchorElem) {
            if (_this.props.onlyShowWhenClipped && !DomUtil_1.isElementOverflownX(anchorElem)) {
                _this.dismissTooltipOverlay();
                return;
            }
            if (Tooltip.displayedTooltip && Tooltip.displayedTooltip.tooltipId !== _this.tooltipId) {
                Tooltip.dismissDisplayedTooltip();
            }
            Tooltip.displayedTooltip = { dismiss: _this.dismissTooltipOverlay, tooltipId: _this.tooltipId };
            _this.refreshContent();
        };
        _this.dismissTooltipOverlay = function () {
            var _a;
            if (_this.tooltipId === ((_a = Tooltip.displayedTooltip) === null || _a === void 0 ? void 0 : _a.tooltipId)) {
                Tooltip.displayedTooltip = undefined;
            }
            _this.clearTimeout();
            _this.setState({ anchorClientRect: undefined, content: undefined, width: undefined });
        };
        _this.getAnchorClientRect = function () { return _this.state.anchorClientRect; };
        return _this;
    }
    Tooltip.dismissDisplayedTooltip = function () {
        if (Tooltip.displayedTooltip) {
            var dismiss = Tooltip.displayedTooltip.dismiss;
            Tooltip.displayedTooltip = undefined;
            try {
                dismiss();
            }
            catch (error) {
                console.warn({ message: 'failed to dismiss displayed tooltip', error: error });
            }
        }
    };
    Tooltip.prototype.componentDidUpdate = function (prevProps) {
        // eslint-disable-next-line deprecation/deprecation
        if (this.state.content && (prevProps.getContent !== this.props.getContent) || (prevProps.tooltipContent !== this.props.tooltipContent)) {
            if (this.delayTimeoutId === undefined) { // don't suddenly show the tooltip if the prop changes during the delay period
                this.refreshContent();
            }
        }
    };
    Tooltip.prototype.componentWillUnmount = function () {
        this.dismissTooltipOverlay();
    };
    Tooltip.prototype.refreshContent = function () {
        var _this = this;
        var _a, _b, _c;
        var setContent = function (content) {
            _this.clearTimeout();
            _this.setState({ content: content, width: undefined }, _this.fixWidth);
        };
        // eslint-disable-next-line deprecation/deprecation
        var contentyThing = (_a = this.props.tooltipContent) !== null && _a !== void 0 ? _a : (_c = (_b = this.props).getContent) === null || _c === void 0 ? void 0 : _c.call(_b);
        if (!contentyThing) {
            this.dismissTooltipOverlay();
        }
        else if (isPromiseLike(contentyThing)) {
            contentyThing
                .then(setContent)
                .catch(function (error) {
                console.warn({ message: "'getContent' promise rejected", error: error });
                _this.dismissTooltipOverlay();
            });
        }
        else {
            setContent(contentyThing);
        }
    };
    Tooltip.prototype.setAnchorClientRectFromEvent = function (_a) {
        var clientX = _a.clientX, clientY = _a.clientY, currentTarget = _a.currentTarget, type = _a.type;
        this.setState({
            anchorClientRect: type === 'focus' || !this.props.followCursor ?
                TooltipConsts_1.adjustClientRectForOffset(currentTarget.getBoundingClientRect()) :
                TooltipConsts_1.adjustClientRectForOffset({
                    bottom: clientY,
                    height: 0,
                    left: clientX,
                    right: clientX,
                    top: clientY,
                    width: 0,
                })
        });
    };
    Tooltip.prototype.renderChildren = function () {
        var _a;
        var children = this.props.children;
        return (React.cloneElement(React.Children.only(children), {
            'aria-describedby': this.state.anchorClientRect ? this.tooltipId : undefined,
            onBlur: this.handleBlur,
            onFocus: this.handleFocus,
            onKeyUp: this.handleKeyUp,
            onMouseEnter: this.handleMouseEnter,
            onMouseLeave: this.handleMouseLeave,
            onMouseMove: this.handleMouseMove,
            tabIndex: (_a = children.props.tabIndex) !== null && _a !== void 0 ? _a : 0,
        }));
    };
    Tooltip.prototype.renderTooltipOverlay = function () {
        var _a = this.state, content = _a.content, width = _a.width, anchorClientRect = _a.anchorClientRect;
        if (!content || !anchorClientRect) {
            return undefined;
        }
        var tooltipSideProps = TooltipConsts_1.TooltipPositionInfoMap[this.props.position];
        /**
         * In order to have the content wrapper grow to fit the tooltip contents, we initially render the wrapper with a width of maxWidth.
         * Once the tooltip content is rendered, we get a reference to the content element and get the actual rendered width. This width is
         * then used, along with the left/right padding and border widths to calculate the width of the content wrapper. This results in a
         * content wrapper that fits to the tooltip content.
         */
        var contentWrapperStyle = tslib_1.__assign(tslib_1.__assign({}, TooltipConsts_1.ContentWrapperStyle), { maxWidth: this.props.maxWidth, overflow: 'hidden', visibility: width === undefined ? 'hidden' : 'visible', width: Math.ceil(width || this.props.maxWidth) });
        return (React.createElement(Overlay_1.Overlay, { allowAutoPosition: true, anchorOrigin: tooltipSideProps.anchorOrigin, disablePointerEvents: true, getAnchorClientRect: this.getAnchorClientRect, shouldAutoPositionOnScrollingRootScroll: this.props.followCursor, stackedOrigin: tooltipSideProps.stackedOrigin, 
            // eslint-disable-next-line deprecation/deprecation
            stackingRootId: this.props.stackingRootId, zIndex: this.props.zIndex },
            React.createElement("div", { "data-tb-test-id": this.props.testId, id: this.tooltipId, ref: this.handleTooltipOverlayContentRef, role: 'tooltip', style: contentWrapperStyle }, React.isValidElement(content) ? content : React.createElement("span", null, content))));
    };
    Tooltip.prototype.render = function () {
        return React.createElement(React.Fragment, null,
            this.renderChildren(),
            this.renderTooltipOverlay());
    };
    Tooltip.defaultProps = {
        children: React.createElement(exports.InfoIcon, null),
        maxWidth: TooltipConsts_1.DefaultMaxWidth,
        position: TooltipConsts_1.TooltipPosition.Right,
    };
    return Tooltip;
}(React.Component));
exports.Tooltip = Tooltip;
//# sourceMappingURL=Tooltip.js.map